/**
 * Calculate Saturn sign transit windows using astronomy-engine
 * MIT licensed, commercial-safe, ±1 day accuracy for ingress/egress
 */

import { DateTime } from 'luxon';
import * as Astronomy from 'astronomy-engine';
import { getAyanamsaProvider, norm360 } from './ayanamsa';

interface SaturnSignTransit {
  currentSign: number;  // 0-11
  ingressDate: string;  // ISO date when Saturn entered current sign
  egressDate: string;   // ISO date when Saturn will exit current sign
}

/**
 * Find Saturn sign ingress/egress dates by querying Swiss Ephemeris
 */
export async function calculateSaturnSignTransits(
  currentDate: DateTime,
  location: { lat: number; lon: number }
): Promise<SaturnSignTransit | null> {
  try {
    const ayanamsaProvider = getAyanamsaProvider('lahiri');
    
    // Get Saturn's current sidereal position using astronomy-engine
    const currentJsDate = currentDate.toJSDate();
    const time = Astronomy.MakeTime(currentJsDate);
    const saturnTrop = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, time, false));
    const ayanamsa = ayanamsaProvider(currentJsDate);
    const currentLonSidereal = norm360(saturnTrop.elon - ayanamsa);
    const currentSign = Math.floor(currentLonSidereal / 30); // 0-11

    // Binary search backward to find ingress (when Saturn entered this sign)
    // Saturn moves ~0.033°/day, so search within ±3 years (max time in one sign)
    const ingressDate = await findSignIngressAstronomyEngine(
      currentSign,
      currentDate,
      location,
      ayanamsaProvider
    );
    
    // Binary search forward to find egress (when Saturn will exit this sign)
    const egressDate = await findSignEgressAstronomyEngine(
      currentSign,
      currentDate,
      location,
      ayanamsaProvider
    );

    // Fallback: estimate from Saturn's current position within the sign
    // Saturn spends ~912 days (2.5 yrs) per sign; use degree-fraction to estimate ingress
    const SATURN_SIGN_DAYS = 912;
    const lonInSign = currentLonSidereal % 30; // degrees through current sign (0–30)
    const estimatedDaysInSign = Math.round((lonInSign / 30) * SATURN_SIGN_DAYS);
    const fallbackIngress = currentDate.minus({ days: estimatedDaysInSign });
    const fallbackEgress = fallbackIngress.plus({ days: SATURN_SIGN_DAYS });

    if (!ingressDate || !egressDate) {
      console.warn('[Saturn Transits] Binary search incomplete; using longitude-based estimation');
      return {
        currentSign,
        ingressDate: fallbackIngress.toISO() || '',
        egressDate: fallbackEgress.toISO() || ''
      };
    }

    return {
      currentSign,
      ingressDate: ingressDate.toISO() || fallbackIngress.toISO() || '',
      egressDate: egressDate.toISO() || fallbackEgress.toISO() || ''
    };

  } catch (error) {
    console.warn('[Saturn Transits] Calculation failed:', error);
    return null;
  }
}

/**
 * Get Saturn's sidereal longitude at a given date using astronomy-engine
 */
function getSaturnSiderealLongitude(
  date: DateTime,
  location: { lat: number; lon: number },
  ayanamsaProvider: (date: Date) => number
): number {
  const jsDate = date.toJSDate();
  const time = Astronomy.MakeTime(jsDate);
  const saturnTrop = Astronomy.Ecliptic(Astronomy.GeoVector(Astronomy.Body.Saturn, time, false));
  const ayanamsa = ayanamsaProvider(jsDate);
  return norm360(saturnTrop.elon - ayanamsa);
}

/**
 * Binary search backward to find when Saturn entered the current sign
 * Achieves ±1 day accuracy using astronomy-engine (MIT licensed)
 */
async function findSignIngressAstronomyEngine(
  targetSign: number,
  startDate: DateTime,
  location: { lat: number; lon: number },
  ayanamsaProvider: (date: Date) => number
): Promise<DateTime | null> {
  // Saturn takes ~2.5 years per sign, search up to 3 years back
  let left = startDate.minus({ years: 3 });
  let right = startDate;
  let result: DateTime | null = null;

  // Binary search with 1-day precision (max 20 iterations)
  for (let i = 0; i < 20; i++) {
    const mid = DateTime.fromMillis((left.toMillis() + right.toMillis()) / 2);
    const sign = Math.floor(getSaturnSiderealLongitude(mid, location, ayanamsaProvider) / 30);

    if (sign === targetSign) {
      result = mid;
      right = mid; // Search earlier for exact ingress
    } else {
      left = mid; // Sign is different, search later
    }

    // Converged to within 1 day
    if (right.diff(left, 'days').days < 1) break;
  }

  return result;
}

/**
 * Binary search forward to find when Saturn will exit the current sign
 */
async function findSignEgressAstronomyEngine(
  targetSign: number,
  startDate: DateTime,
  location: { lat: number; lon: number },
  ayanamsaProvider: (date: Date) => number
): Promise<DateTime | null> {
  // Saturn takes ~2.5 years per sign, search up to 3 years forward
  let left = startDate;
  let right = startDate.plus({ years: 3 });
  let result: DateTime | null = null;

  // Binary search with 1-day precision (max 20 iterations)
  for (let i = 0; i < 20; i++) {
    const mid = DateTime.fromMillis((left.toMillis() + right.toMillis()) / 2);
    const sign = Math.floor(getSaturnSiderealLongitude(mid, location, ayanamsaProvider) / 30);

    if (sign === targetSign) {
      result = mid;
      left = mid; // Still in sign, search later for egress
    } else {
      right = mid; // Sign changed, search earlier for exact egress
    }

    // Converged to within 1 day
    if (right.diff(left, 'days').days < 1) break;
  }

  return result;
}
